import { useWorkbook } from '@/context/WorkbookContext';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronLeft, ChevronRight, Bookmark, CheckCircle2 } from 'lucide-react';

export const WorkbookPage = ({ 
  children, 
  pageId,
  prevPage,
  nextPage,
  showNavigation = true,
  className = ''
}) => {
  const { 
    setCurrentPage, 
    markPageComplete, 
    toggleBookmark, 
    formData 
  } = useWorkbook();
  
  const isComplete = formData.completedPages?.includes(pageId);
  const isBookmarked = formData.bookmarks?.includes(pageId);
  
  return (
    <div className={`workbook-page ${className}`}>
      {/* Page content */}
      <div className="max-w-3xl mx-auto">
        {children}
      </div>
      
      {/* Page footer with navigation */}
      {showNavigation && (
        <div className="max-w-3xl mx-auto mt-16 pt-8 border-t border-border">
          <div className="flex items-center justify-between gap-4">
            {/* Previous */}
            <div className="flex-1">
              {prevPage && (
                <Button
                  variant="ghost"
                  onClick={() => setCurrentPage(prevPage)}
                  className="gap-2"
                >
                  <ChevronLeft className="h-4 w-4" />
                  <span className="hidden sm:inline">Previous</span>
                </Button>
              )}
            </div>
            
            {/* Center actions */}
            <div className="flex items-center gap-3">
              <Button
                variant={isBookmarked ? "secondary" : "ghost"}
                size="icon"
                onClick={() => toggleBookmark(pageId)}
                title={isBookmarked ? "Remove bookmark" : "Add bookmark"}
              >
                <Bookmark className={`h-4 w-4 ${isBookmarked ? 'fill-current' : ''}`} />
              </Button>
              
              <Button
                variant={isComplete ? "secondary" : "outline"}
                size="sm"
                onClick={() => markPageComplete(pageId)}
                className="gap-2"
              >
                <CheckCircle2 className={`h-4 w-4 ${isComplete ? 'text-success' : ''}`} />
                {isComplete ? 'Completed' : 'Mark Complete'}
              </Button>
            </div>
            
            {/* Next */}
            <div className="flex-1 flex justify-end">
              {nextPage && (
                <Button
                  onClick={() => setCurrentPage(nextPage)}
                  className="gap-2"
                >
                  <span className="hidden sm:inline">Next</span>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Reusable components for workbook pages

export const RuleHeader = ({ ruleNumber, title, section }) => {
  const sectionColors = {
    'stability': 'text-[hsl(var(--section-stability))]',
    'control': 'text-[hsl(var(--section-control))]',
    'growth': 'text-[hsl(var(--section-growth))]',
    'healing': 'text-[hsl(var(--section-healing))]',
  };
  
  return (
    <header className="mb-10">
      <div className="flex items-center gap-3 mb-4">
        <span className={`text-sm font-semibold uppercase tracking-wider ${sectionColors[section] || 'text-primary'}`}>
          Rule {ruleNumber}
        </span>
      </div>
      <h1 className="text-3xl md:text-4xl font-serif font-bold text-foreground leading-tight">
        {title}
      </h1>
    </header>
  );
};

export const SectionTitle = ({ children, className = '' }) => (
  <h2 className={`text-xl font-serif font-bold text-foreground mb-4 ${className}`}>
    {children}
  </h2>
);

export const Paragraph = ({ children, className = '' }) => (
  <p className={`text-base text-foreground leading-relaxed mb-4 ${className}`}>
    {children}
  </p>
);

export const Callout = ({ type = 'info', title, children }) => {
  const typeStyles = {
    info: 'callout-info',
    success: 'callout-success',
    warning: 'callout-warning',
    tip: 'callout-tip',
  };
  
  return (
    <div className={`callout ${typeStyles[type]} mb-6`}>
      {title && <p className="font-semibold mb-2">{title}</p>}
      <div className="text-sm">{children}</div>
    </div>
  );
};

export const WorksheetInput = ({ label, placeholder, value, onChange, multiline = false }) => (
  <div className="mb-4">
    {label && <label className="block text-sm font-medium text-foreground mb-2">{label}</label>}
    {multiline ? (
      <textarea
        className="worksheet-textarea"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={4}
      />
    ) : (
      <input
        type="text"
        className="worksheet-input"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    )}
  </div>
);

export const CheckboxItem = ({ checked, onChange, children }) => (
  <label className="flex items-start gap-3 cursor-pointer group mb-3">
    <Checkbox 
      checked={checked} 
      onCheckedChange={onChange}
      className="mt-0.5"
    />
    <span className="text-sm text-foreground leading-relaxed group-hover:text-primary transition-colors">
      {children}
    </span>
  </label>
);

export const StepList = ({ steps }) => (
  <div className="space-y-4 mb-8">
    {steps.map((step, index) => (
      <div key={index} className="flex gap-4">
        <div className="step-number">{index + 1}</div>
        <div className="flex-1 pt-1">
          <p className="text-foreground">{step}</p>
        </div>
      </div>
    ))}
  </div>
);

export const ExampleBox = ({ title, children }) => (
  <div className="bg-secondary/50 rounded-lg p-5 mb-6">
    <p className="font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-3">
      {title || 'Example'}
    </p>
    <div className="text-sm text-foreground">{children}</div>
  </div>
);

export const AffirmationBox = ({ children }) => (
  <div className="affirmation-box my-8">
    <p className="text-lg font-serif text-foreground">{children}</p>
  </div>
);

export const RecapBox = ({ items }) => (
  <div className="recap-box">
    <p className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-4">
      Quick Recap
    </p>
    <ul className="space-y-2">
      {items.map((item, index) => (
        <li key={index} className="flex items-start gap-2 text-sm">
          <span className="text-primary mt-1">✓</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

export const OptionalSection = ({ title, children }) => (
  <div className="optional-section">
    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
      {title || 'OPTIONAL: Reflection / Faith Note'}
    </p>
    <div className="text-sm text-muted-foreground">{children}</div>
  </div>
);

// ---------- Money helpers & auto-calculating totals ----------

export const parseMoney = (val) => {
  if (val === null || val === undefined) return 0;
  const n = parseFloat(String(val).replace(/[^0-9.\-]/g, ''));
  return isNaN(n) ? 0 : n;
};

export const sumMoney = (items, field, filterFn) => {
  if (!Array.isArray(items)) return 0;
  let total = 0;
  for (let i = 0; i < items.length; i++) {
    if (filterFn && !filterFn(items[i])) continue;
    total += parseMoney(items[i][field]);
  }
  return total;
};

export const formatMoney = (num) => {
  const rounded = Math.round((Number(num) || 0) * 100) / 100;
  const hasCents = rounded % 1 !== 0;
  return '$' + rounded.toLocaleString('en-US', {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  });
};

export const AutoTotal = ({ label, value, hint, testId = 'auto-total' }) => (
  <div className="flex items-center justify-between gap-4 bg-primary/10 border border-primary/20 rounded-lg px-4 py-3">
    <div>
      <p className="text-sm font-medium text-foreground">{label}</p>
      {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
    </div>
    <p className="text-lg font-bold text-primary tabular-nums shrink-0" data-testid={testId}>
      {formatMoney(value)}
    </p>
  </div>
);
